import { social, type Overview } from './api';
import { finite, matchingSummary, poolTrades, swapAmounts } from './icpswap';

export const HOUR_MS = 3_600_000;
export const VOLUME_REFRESH_MS = 60_000;
export type VolumePeriod = '1h' | '24h' | '7d';
export const VOLUME_WINDOWS_MS: Record<VolumePeriod, number> = { '1h': HOUR_MS, '24h': 24 * HOUR_MS, '7d': 7 * 24 * HOUR_MS };
const PAGE_SIZE = 100;
// A scan limit is a safety limit, never evidence that the whole window was read.
const MAX_PAGES: Record<VolumePeriod, number> = { '1h': 20, '24h': 60, '7d': 100 };

export interface VolumeToken { market: string; ledger: string; base: string; name: string; symbol: string; pool: string | null }
export interface CurveVolumeTrade { id: bigint; at: bigint; market: string; baseLedger: string | null; dollars: number | null }
export interface HourTrade { key: string; at: number; dollars: number | null }
export interface HourRead<T> { trades: T[]; complete: boolean }
export interface VolumeRow { market: string; name: string; symbol: string; dollars: number; trades: number }
export interface VolumeValue { dollars: number; trades: number | null }
export interface PoolVolumeAggregate { dollars: number | null; trades: number | null }
export interface RollingVolume {
  /** Top ten fully covered hourly volumes, for the existing one-hour ticker. */
  rows: VolumeRow[];
  /** Every registry token: verified zero is an object, unavailable coverage is null. */
  volumes: Map<string, VolumeValue | null>;
  period: VolumePeriod; asOf: number; from: number; covered: number; total: number; complete: boolean;
}

const validDollars = (value: number | null): value is number => value !== null && Number.isFinite(value) && value >= 0;
const validCount = (value: number | null): value is number => value !== null && Number.isSafeInteger(value) && value >= 0;

/** Scan the shared, globally numbered curve tape, rather than querying every token. */
export async function readCurveWindow(read: (before: bigint | undefined, limit: number) => Promise<CurveVolumeTrade[]>, asOf: number, period: VolumePeriod = '1h', maxPages = MAX_PAGES[period]): Promise<HourRead<CurveVolumeTrade>> {
  const until = BigInt(asOf) * 1_000_000n, from = until - BigInt(VOLUME_WINDOWS_MS[period]) * 1_000_000n;
  const trades: CurveVolumeTrade[] = [];
  let previous: CurveVolumeTrade | undefined;
  for (let page = 0; page < maxPages; page++) {
    const rows = await read(previous?.id, PAGE_SIZE);
    if (!rows.length) return { trades, complete: previous === undefined || previous.id === 0n };
    for (const row of rows) {
      // A missing ID or a cursor which does not advance cannot prove window coverage.
      if (previous && (row.id + 1n !== previous.id || row.at > previous.at)) return { trades, complete: false };
      if (row.id < 0n || row.at < 0n) return { trades, complete: false };
      if (row.at >= from && row.at <= until) trades.push(row);
      previous = row;
    }
    if (previous!.at < from || previous!.id === 0n) return { trades, complete: true };
  }
  return { trades, complete: false };
}

export function readCurveHour(read: (before: bigint | undefined, limit: number) => Promise<CurveVolumeTrade[]>, asOf: number, maxPages = MAX_PAGES['1h']) {
  return readCurveWindow(read, asOf, '1h', maxPages);
}

/** Offset pages can overlap as new swaps arrive. Deduplicate, and never treat a cap as exhaustion. */
export async function readPoolWindow(read: (page: number, limit: number) => Promise<{ trades: HourTrade[]; total: number; page: number }>, asOf: number, period: VolumePeriod = '1h', maxPages = MAX_PAGES[period]): Promise<HourRead<HourTrade>> {
  const from = asOf - VOLUME_WINDOWS_MS[period], trades: HourTrade[] = [], seen = new Map<string, HourTrade>();
  let previousAt = Infinity, previousOldest = Infinity;
  for (let page = 1; page <= maxPages; page++) {
    const result = await read(page, PAGE_SIZE);
    if (result.page !== page || !Number.isSafeInteger(result.total) || result.total < 0) return { trades, complete: false };
    if (!result.trades.length) return { trades, complete: seen.size >= result.total };
    for (const row of result.trades) {
      if (!row.key || !Number.isSafeInteger(row.at) || row.at < 0) return { trades, complete: false };
      const duplicate = seen.get(row.key);
      if (duplicate) {
        if (duplicate.at !== row.at || !Object.is(duplicate.dollars, row.dollars)) return { trades, complete: false };
        continue;
      }
      if (row.at > previousAt) return { trades, complete: false };
      seen.set(row.key, row); previousAt = row.at;
      if (row.at >= from && row.at <= asOf) trades.push(row);
    }
    if (previousAt < from || seen.size >= result.total) return { trades, complete: true };
    if (previousAt >= previousOldest) return { trades, complete: false };
    previousOldest = previousAt;
  }
  return { trades, complete: false };
}

export function readPoolHour(read: (page: number, limit: number) => Promise<{ trades: HourTrade[]; total: number; page: number }>, asOf: number, maxPages = MAX_PAGES['1h']) {
  return readPoolWindow(read, asOf, '1h', maxPages);
}

/** Exclude a token when any part of its window is unknown; a partial total must not win a rank. */
export function rankRollingVolume(tokens: VolumeToken[], curve: HourRead<CurveVolumeTrade>, pools: Map<string, HourRead<HourTrade>>, asOf: number, period: VolumePeriod = '1h', aggregates = new Map<string, PoolVolumeAggregate>()): RollingVolume {
  const rows: VolumeRow[] = [], byMarket = new Map(tokens.map(token => [token.market, token]));
  const totals = new Map<string, { dollars: number; trades: number }>(), unknown = new Set<string>(), seenCurve = new Map<bigint, CurveVolumeTrade>();
  const from = asOf - VOLUME_WINDOWS_MS[period], untilNs = BigInt(asOf) * 1_000_000n, fromNs = BigInt(from) * 1_000_000n;
  for (const trade of curve.trades) {
    if (trade.at < fromNs || trade.at > untilNs) continue;
    const token = byMarket.get(trade.market);
    if (!token) continue; // A launch after the registry snapshot appears on the next refresh.
    const duplicate = seenCurve.get(trade.id);
    if (duplicate) {
      if (duplicate.at !== trade.at || duplicate.market !== trade.market || duplicate.baseLedger !== trade.baseLedger || !Object.is(duplicate.dollars, trade.dollars)) {
        unknown.add(duplicate.market); unknown.add(trade.market);
      }
      continue;
    }
    seenCurve.set(trade.id, trade);
    if (trade.baseLedger !== token.base || !validDollars(trade.dollars)) { unknown.add(token.market); continue; }
    const total = totals.get(token.market) ?? { dollars: 0, trades: 0 };
    total.dollars += trade.dollars; total.trades++;
    totals.set(token.market, total);
  }
  const volumes = new Map<string, VolumeValue | null>(tokens.map(token => [token.market, null]));
  let covered = 0;
  for (const token of tokens) {
    if (!curve.complete || unknown.has(token.market)) continue;
    const curveTotal = totals.get(token.market) ?? { dollars: 0, trades: 0 };
    const total: VolumeValue = { ...curveTotal };
    if (token.pool) {
      if (period !== '1h') {
        // ICPSwap publishes indexed 24h/7d USD aggregates. Do not replay its entire swap history.
        const pool = aggregates.get(token.market);
        if (!pool || !validDollars(pool.dollars)) continue;
        total.dollars += pool.dollars;
        total.trades = validCount(pool.trades) ? curveTotal.trades + pool.trades : null;
      } else {
        const pool = pools.get(token.market);
        if (!pool?.complete) continue;
        let valid = true;
        const seen = new Map<string, HourTrade>();
        for (const trade of pool.trades) {
          if (trade.at < from || trade.at > asOf) continue;
          const duplicate = seen.get(trade.key);
          if (duplicate) {
            if (duplicate.at !== trade.at || !Object.is(duplicate.dollars, trade.dollars)) { valid = false; break; }
            continue;
          }
          seen.set(trade.key, trade);
          if (!validDollars(trade.dollars)) { valid = false; break; }
          total.dollars += trade.dollars; total.trades = (total.trades ?? 0) + 1;
        }
        if (!valid) continue;
      }
    }
    if (!Number.isFinite(total.dollars)) continue;
    covered++; volumes.set(token.market, total);
    if (period === '1h' && total.trades && total.dollars > 0) rows.push({ market: token.market, name: token.name, symbol: token.symbol, dollars: total.dollars, trades: total.trades });
  }
  rows.sort((a, b) => b.dollars - a.dollars || a.market.localeCompare(b.market));
  return { rows: rows.slice(0, 10), volumes, period, asOf, from, covered, total: tokens.length, complete: covered === tokens.length };
}

async function mapBounded<T>(values: T[], work: (value: T) => Promise<void>, concurrency: number) {
  let cursor = 0;
  await Promise.all(Array.from({ length: Math.min(concurrency, values.length) }, async () => {
    while (cursor < values.length) await work(values[cursor++]);
  }));
}

// Spread requests across time as well as limiting concurrent pool reads. Cached pages are shared.
let nextPoolStart = 0;
async function pacedPoolRead<T>(read: () => Promise<T>): Promise<T> {
  const now = Date.now(), wait = Math.max(0, nextPoolStart - now);
  nextPoolStart = Math.max(now, nextPoolStart) + 350;
  if (wait) await new Promise(resolve => setTimeout(resolve, wait));
  return read();
}

interface VolumeCache {
  key: string; expires: number; asOf: number; pending: number;
  curvePages: Map<string, Promise<CurveVolumeTrade[]>>;
  poolPages: Map<string, Promise<{ trades: HourTrade[]; total: number; page: number }>>;
  results: Map<string, Promise<RollingVolume>>;
}
let cache: VolumeCache | undefined;

/** All periods share the same minute's raw curve pages, including the ticker's hourly read. */
export function loadRollingVolume(overview: Overview, period: VolumePeriod = '1h'): Promise<RollingVolume> {
  const tokens: VolumeToken[] = overview.launches.map(row => ({ market: row.market.toText(), ledger: row.ledger.toText(), base: row.base.toText(), name: row.name, symbol: row.symbol, pool: row.icpswap_pool[0]?.toText() ?? null }));
  const key = JSON.stringify(tokens.map(token => [token.market, token.ledger, token.base, token.pool, token.name, token.symbol]).sort((a, b) => a[0]!.localeCompare(b[0]!)));
  const now = Date.now();
  if (!cache || cache.key !== key || (cache.expires <= now && !cache.pending)) {
    cache = { key, expires: now + VOLUME_REFRESH_MS, asOf: now, pending: 0, curvePages: new Map(), poolPages: new Map(), results: new Map() };
  }
  const snapshot = cache, asOf = snapshot.asOf, aggregates = new Map<string, PoolVolumeAggregate>();
  if (period !== '1h') for (const token of tokens.filter(token => token.pool)) {
    const candidate = matchingSummary(overview.pools.get(token.pool!), token.ledger, token.base);
    const pool = candidate?.poolId === token.pool ? candidate : undefined;
    aggregates.set(token.market, { dollars: finite(period === '24h' ? pool?.volumeUSD24H : pool?.volumeUSD7D), trades: period === '24h' ? finite(pool?.txCount24H) : null });
  }
  // Overview refreshes may bring a newer analytics snapshot before raw-page expiry.
  const resultKey = `${period}:${JSON.stringify([...aggregates])}`;
  const existing = snapshot.results.get(resultKey);
  if (existing) return existing;
  snapshot.pending++;
  const promise = (async () => {
    let service: ReturnType<typeof social> | undefined;
    const curve = readCurveWindow((before, limit) => {
      const pageKey = before?.toString() ?? 'latest';
      let page = snapshot.curvePages.get(pageKey);
      if (!page) {
        service ??= social();
        page = service.then(async actor => (await actor.recent_trades(before === undefined ? [] : [before], limit)).map(row => ({ id: row.id, at: row.at, market: row.market.toText(), baseLedger: row.base_ledger[0]?.toText() ?? null, dollars: row.usd[0] ?? null }))).catch(error => {
          if (snapshot.curvePages.get(pageKey) === page) snapshot.curvePages.delete(pageKey);
          throw error;
        });
        snapshot.curvePages.set(pageKey, page);
      }
      return page;
    }, asOf, period).catch(() => ({ trades: [], complete: false }));
    const pools = new Map<string, HourRead<HourTrade>>();
    await Promise.all([
      curve,
      period === '1h' ? mapBounded(tokens.filter(token => token.pool), async token => {
        try {
          const result = await readPoolHour((page, limit) => {
            const pageKey = `${token.pool}:${token.ledger}:${token.base}:${page}`;
            let response = snapshot.poolPages.get(pageKey);
            if (!response) {
              response = pacedPoolRead(async () => {
                const result = await poolTrades(token.pool!, undefined, page, limit);
                return { total: result.totalElements, page: result.page, trades: result.content.map(trade => {
                  if (trade.poolId !== token.pool || trade.actionType !== 'Swap') throw Error('Unexpected pool trade.');
                  return { key: trade.txHash, at: trade.txTime, dollars: swapAmounts(trade, token.ledger, token.base).dollars };
                }) };
              }).catch(error => {
                if (snapshot.poolPages.get(pageKey) === response) snapshot.poolPages.delete(pageKey);
                throw error;
              });
              snapshot.poolPages.set(pageKey, response);
            }
            return response;
          }, asOf);
          pools.set(token.market, result);
        } catch { pools.set(token.market, { trades: [], complete: false }); }
      }, 3) : Promise.resolve(),
    ]);
    const result = rankRollingVolume(tokens, await curve, pools, asOf, period, aggregates);
    if (!result.covered) throw Error(`Past-${period} trade coverage is temporarily unavailable.`);
    return result;
  })().catch(error => {
    if (snapshot.results.get(resultKey) === promise) snapshot.results.delete(resultKey);
    throw error;
  }).finally(() => { snapshot.pending--; });
  snapshot.results.set(resultKey, promise);
  return promise;
}
